
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';

import SideBarLayout from '../../../global/layout/SideBarLayout';
import Loader from '../../../global/ui/Loader';

import { MyContext } from '../../../context';
import { ApiResponseType } from '../../../types';
import { canViewLocation } from '../../../data/data';
import { endPoints } from '../../../utils/endpoints';
import { emptyFunction, getCompanyName, showToast } from '../../../utils';
import {
    makeDeleteCall,
    makeGetCall,
    makeNextPageCall,
    makePostCall
} from '../../../effects/requests';

import LocationVaultHeader from './LocationVaultHeader';
import LocationVaultFilterTabs, { TabType } from './LocationVaultFilterTabs';
import LocationVaultRightAction from './LocationVaultRightAction';
import LocationVaultGridView from './LocationVaultGridView';
import LocationVaultListView from './LocationVaultListView';
import DeleteLocPopup from './DeleteLocPopup';
import LocationSharing from './LocationSharing';
import AsignLocationToProd from './AsignLocationToProd';

import CreateLocation from '../createLocation';
import ScoutingVault from '../scouting/ScoutingVault';
import ScoutingDetails from '../scouting/ScoutingDetails';
import PerScript from '../perScript';
import CreateScript from '../perScript/createScript';
import Collections from '../collections';
import CreateCollections from '../collections/CreateCollections';
import AsignLocationToCollection from '../collections/AsignLocationToCollection';
import AsignLocationToPerScript from '../perScript/AsignLocationToPerScript';
import PerScriptGridView from '../perScript/PerScriptGridView';
import PerScriptCollection from '../perScript/PerScriptCollection';
import ImageFullScreenViewer from '../../../global/ImageFullScreenViewer';

// --------------------------------------------------
// Reusable UI Components
// --------------------------------------------------

const Dummy = () => (
    <div
        style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '18px',
            color: 'black',
            textAlign: 'center',
            width: '100%',
            padding: '0 16px',
            boxSizing: 'border-box',
            fontWeight: 600
        }}
    >
        Coming Soon, Please visit{' '}
        <a
            href="https://www.sixthdegree.io/locations"
            target="_blank"
            rel="noopener noreferrer"
        >
            www.sixthdegree.io/locations
        </a>{' '}
        for locations already on baord.
    </div>
);

const InfiniteScrollLoader = () => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}
    >
        <Loader imgStyle={{ width: '40px', height: '40px' }} />
    </div>
);

// --------------------------------------------------
// Main Component
// --------------------------------------------------

const LocationVault = () => {
    const { userData } = useContext(MyContext);
    const { type: vaultType = '' } = useParams();

    const navigate = useNavigate();

    // --------------------------------------------------
    // UI State
    // --------------------------------------------------

    const [isGridView, setIsGridView] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('studio_vault');

    const [showCreateLoc, setShowCreateLoc] = useState(false);
    const [showCreateScript, setShowCreateScript] = useState(false);
    const [showCreateCollection, setShowCreateCollection] = useState(false);

    const [isShareLoc, setIsShareLoc] = useState(false);

    // --------------------------------------------------
    // Location State
    // --------------------------------------------------

    const [allLocationData, setAllLocationData] = useState<any[]>([]);
    const [nextUrl, setNextUrl] = useState<string | null>(null);

    const [searchQuery, setSearchQuery] = useState<string | null>('');

    const [selectedLocId, setSelectedLocId] = useState<any>(null);
    const [selectedLoc, setSelectedLoc] = useState<any>(null);

    const [asignLocData, setAsignLocData] = useState<any>(null);
    const [asignLocCollection, setAsignLocCollection] = useState<any>(null);
    const [asignLocToScript, setAsignLocToScript] = useState<any>(null);

    const [locationDetails, setLocationDetails] = useState<any>(null);
    const [collectionDetails, setCollectionDetails] = useState<any>(null);
    const [perScriptCollectionDetails, setPerScriptCollectionDetails] = useState<any>(null);
    const [perScriptSelectedImages, setPerScriptSelectedImages] = useState<any>([])
    const [viewerOpen, setViewerOpen] = useState(false);

    // --------------------------------------------------
    // Script / Collection State
    // --------------------------------------------------

    const [collectionList, setCollectionList] = useState<any[]>([]);
    const [perScriptList, setPerScriptList] = useState<any[]>([]);

    const [scriptNextUrl, setScriptNextUrl] = useState<string | null>(null);
    const [collectionNextUrl, setCollectionNextUrl] = useState<string | null>(null);

    // --------------------------------------------------
    // Derived Values
    // --------------------------------------------------

    const companyName = getCompanyName(userData);

    const isStudioVault = activeTab === 'studio_vault';
    const isScouting = activeTab === 'scouting';
    const isPerScript = activeTab === 'per_script';
    const isCollections = activeTab === 'collections';

    const isLocationTab = isStudioVault || isScouting;

    // --------------------------------------------------
    // Fetch Methods
    // --------------------------------------------------

    const fetchLocation = (searchValue = '') => {
        const params = {
            production_company_name: getCompanyName(userData),
            search: searchValue,
            my_vault: vaultType == 'myvault' ? true : false,
            collection_id: collectionDetails?.id || ''
        };

        makeGetCall(endPoints.LOCATION_VAULT, params)
            .then((res: ApiResponseType<any>) => {
                if (res?.status?.code === 200) {
                    setAllLocationData(res?.results);
                    setNextUrl(res?.next || null);
                    setAsignLocData(null);
                }
            })
            .catch(emptyFunction);
    };

    const fetchCollection = () => {
        makeGetCall(endPoints.LOCATION_COLLECTION, {
            production_company_name: getCompanyName(userData)
        }).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                const data = res?.results?.length > 0 ? res.results : [];

                setCollectionList(data);
                setCollectionNextUrl(res?.next || null);
            }
        });
    };

    const fetchScript = () => {
        const url = `${endPoints.LOCATION_SCRIPT}?production_company_name=${getCompanyName(userData)}`;

        makeGetCall(url)
            .then((res: ApiResponseType<any>) => {
                if (res?.status?.code === 200) {
                    setPerScriptList(
                        res?.results?.length > 0 ? res.results : []
                    );
                    setScriptNextUrl(res?.next || null);
                }
            })
            .catch(emptyFunction);
    };

    // --------------------------------------------------
    // Pagination
    // --------------------------------------------------

    const fetchMoreData = () => {
        if (!nextUrl) return;

        makeNextPageCall(nextUrl, false, true).then(
            (res: ApiResponseType<any>) => {
                setAllLocationData([...allLocationData, ...res.results]);
                setNextUrl(res?.next);
            }
        );
    };

    const fetchMoreScript = () => {
        if (!scriptNextUrl) return;

        makeNextPageCall(scriptNextUrl, false, true).then(
            (res: ApiResponseType<any>) => {
                setPerScriptList([...perScriptList, ...res.results]);
                setScriptNextUrl(res?.next);
            }
        );
    };

    const fetchMoreCollection = () => {
        if (!collectionNextUrl) return;

        makeNextPageCall(collectionNextUrl, false, true).then(
            (res: ApiResponseType<any>) => {
                setCollectionList([...collectionList, ...res.results]);
                setCollectionNextUrl(res?.next);
            }
        );
    };

    // --------------------------------------------------
    // Location Actions
    // --------------------------------------------------

    const handleRemoveLoc = () => {
        const url = `${endPoints.LOCATION_VAULT}${selectedLocId}/`;

        makeDeleteCall(url)
            .then((res: ApiResponseType<any>) => {
                if (res?.status?.code === 200) {
                    showToast(res?.status?.message, 'success');

                    setAllLocationData(prev =>
                        prev.filter(location => location?.id !== selectedLocId)
                    );
                }

                setSelectedLocId(null);
            })
            .catch(() => setSelectedLocId(null));
    };

    const moveItem = (from: number, to: number) => {
        setAllLocationData(prev => {
            const updated = [...prev];
            const [removed] = updated.splice(from, 1);

            updated.splice(to, 0, removed);

            return updated;
        });
    };

    const handleViewLocation = (location: any) => {
        navigate(`/location-folders/${vaultType}/${location?.id}`, {
            state: {
                location: {
                    ...location?.location,
                    role: location?.role,
                    can_view_location:
                        location?.can_view_location || canViewLocation
                }
            }
        });
    };

    const handleShareLocation = (data: any) => {
        setSelectedLoc(data);
        setIsShareLoc(true);
    };

    const onAsign = (data: any) => {
        if (vaultType == 'vault') {
            setAsignLocCollection(data);
            return;
        }

        setAsignLocData(data);
    };

    const handleApply = async (selectedData: any, locationId: any) => {
        if (!selectedData?.id) {
            showToast('Please add at least one production', 'error');
            return;
        }

        const payload = {
            production_company_id: selectedData?.production_company?.id,
            location_vault_id: locationId
        };

        const toastId = toast.loading('Duplicating location vault...', {
            position: 'bottom-right',
            autoClose: false,
            closeOnClick: false,
            closeButton: false
        });

        setAsignLocData(null);

        const res: ApiResponseType<any> = await makePostCall(
            endPoints.DUPLICATE_LOCATION_VAULT,
            payload,
            false,
            true
        );

        toast.dismiss(toastId);

        if (res?.status?.code == 200) {
            showToast(res?.status?.message, 'success');
            fetchLocation();
        }
    };

    // --------------------------------------------------
    // Header / Tab Actions
    // --------------------------------------------------

    const handleCreate = (type: any) => {
        if (type == 'location') {
            setShowCreateLoc(true);
            return;
        }

        if (type == 'script') {
            setShowCreateScript(true);
            return;
        }

        if (type == 'collection') {
            setShowCreateCollection(true);
            return;
        }
    };

    const handleTabChange = (type: TabType) => {
        setActiveTab(type);
        setCollectionDetails(null);
        setPerScriptCollectionDetails(null)
    };

    // Preserve existing 800ms debounce behavior.
    const onChangeTextDebounce = useCallback(
        debounce(fetchLocation, 800),
        []
    );

    const handleSearch = (value: any) => {
        setSearchQuery(value);
        onChangeTextDebounce(value);
    };

    // --------------------------------------------------
    // Initial / Tab Data Fetch
    // --------------------------------------------------

    useEffect(() => {
        if (!userData?.id) return;

        if (activeTab === 'studio_vault' || activeTab === 'scouting') {
            fetchLocation();
        }

        if (activeTab === 'per_script') {
            fetchScript();
        }

        if (activeTab === 'collections') {
            fetchCollection();
        }
    }, [userData?.id, vaultType, activeTab]);

    const handleImageShow = (item: any, episodeId: any) => {
        makeGetCall(endPoints.SCRIPT_SCENE_COLLECTION_IMAGES, { collection_id: episodeId, location_id: item?.id }).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                const data = res?.results?.length > 0 ? res?.results : [];
                if (data?.length <= 0) {
                    showToast('No image found.', 'error');
                    return
                }
                setPerScriptSelectedImages(data)
                setViewerOpen(true)
            }
        })
    }

    // --------------------------------------------------
    // Reusable View Rendering
    // --------------------------------------------------

    const renderLocationGrid = () => (
        <LocationVaultGridView
            onView={isScouting ? setLocationDetails : handleViewLocation}
            onImageView={isScouting ? setLocationDetails : handleViewLocation}
            allLocationData={allLocationData}
            onDelete={(loc: any) => setSelectedLocId(loc?.id)}
            moveItem={moveItem}
            onShare={handleShareLocation}
            vaultType={vaultType}
            onAsign={onAsign}
            onScriptAsign={setAsignLocToScript}
            {...(isScouting ? { isScouted: true } : {})}
            {...(isStudioVault ? { hideAsign: collectionDetails?.id } : {})}
        />
    );

    const renderLocationList = () => {
        if (isScouting) {
            return (
                <ScoutingVault
                    allLocationData={allLocationData}
                    onShare={handleShareLocation}
                    vaultType={vaultType}
                    onAsign={onAsign}
                    onScriptAsign={setAsignLocToScript}
                    onReachEnd={fetchMoreData}
                />
            );
        }

        return (
            <LocationVaultListView
                onView={handleViewLocation}
                onImageView={handleViewLocation}
                allLocationData={allLocationData}
                onDelete={(loc: any) => setSelectedLocId(loc?.id)}
                moveItem={moveItem}
                onShare={handleShareLocation}
                vaultType={vaultType}
                onAsign={onAsign}
                onScriptAsign={setAsignLocToScript}
                hideAsign={collectionDetails?.id}
            />
        );
    };

    const renderInfiniteScroll = (
        dataLength: number,
        next: () => void,
        hasMore: boolean,
        children: React.ReactNode
    ) => (
        <InfiniteScroll
            dataLength={dataLength}
            next={next}
            hasMore={hasMore}
            loader={null}
            // loader={<InfiniteScrollLoader />}
            scrollableTarget="scrollableId"
        >
            {children}
        </InfiniteScroll>
    );

    const renderTabContent = () => {
        if (isLocationTab) {
            return (!isGridView && isScouting) ? renderLocationList() : renderInfiniteScroll(
                allLocationData?.length,
                fetchMoreData,
                !!nextUrl,
                isGridView ? renderLocationGrid() : renderLocationList()
            );
        }

        if (isPerScript) {
            if (perScriptCollectionDetails?.id) {
                return <PerScriptCollection
                    collectionDetails={perScriptCollectionDetails}
                    onClick={handleImageShow}
                    onBack={() => setPerScriptCollectionDetails(null)}
                />
            }
            return renderInfiniteScroll(
                perScriptList?.length,
                fetchMoreScript,
                !!scriptNextUrl,
                isGridView
                    ?
                    <PerScriptGridView
                        allData={perScriptList}
                        onRefresh={fetchScript}
                        onView={(data: any) => setPerScriptCollectionDetails(data)}
                    />
                    :
                    <PerScript
                        allData={perScriptList}
                        onRefresh={fetchScript}
                        onView={(data: any) => setPerScriptCollectionDetails(data)}
                    />
            );
        }

        if (isCollections) {
            return renderInfiniteScroll(
                collectionList?.length,
                fetchMoreCollection,
                !!collectionNextUrl,
                <Collections
                    allData={collectionList}
                    onRefresh={fetchCollection}
                    myVault={vaultType == 'myvault'}
                />
            );
        }

        return <Dummy />;
    };

    // --------------------------------------------------
    // Render
    // --------------------------------------------------

    return (
        <SideBarLayout>
            <div className="right-content-full-screen">
                <div id="scrollableId" className="lv-page">

                    <LocationVaultHeader
                        onCreate={handleCreate}
                        query={searchQuery}
                        onSearch={handleSearch}
                        vaultType={vaultType}
                    />

                    <div className="lv-tabs-bar">
                        <LocationVaultFilterTabs
                            activeTab={activeTab}
                            setActiveTab={handleTabChange}
                        />

                        <LocationVaultRightAction
                            isGridView={isGridView}
                            onViewSwitch={setIsGridView}
                            onEdit={emptyFunction}
                            isScouting={isScouting}
                            hideViewToggle={isCollections}
                        />
                    </div>

                    {collectionDetails?.name && (
                        <div
                            style={{ marginBottom: 10 }}
                            className="ld-breadcrumb"
                        >
                            <button className="ld-breadcrumb__link">
                                Collection
                            </button>

                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <polyline points="9 18 15 12 9 6" />
                            </svg>

                            <span className="ld-breadcrumb__current">
                                {collectionDetails?.name || ''}
                            </span>
                        </div>
                    )}

                    {/* Main Tab Content */}
                    {renderTabContent()}

                    {/* Create Location */}
                    {showCreateLoc && (
                        <CreateLocation
                            onClose={() => setShowCreateLoc(false)}
                            onDone={() => {
                                setShowCreateLoc(false);
                                fetchLocation();
                            }}
                            vaultType={vaultType}
                        />
                    )}

                    {/* Delete Location */}
                    {selectedLocId && (
                        <DeleteLocPopup
                            onClose={() => setSelectedLocId(null)}
                            onDelete={handleRemoveLoc}
                        />
                    )}

                    {/* Share Location */}
                    {isShareLoc && (
                        <LocationSharing
                            isOpen={isShareLoc}
                            onClose={() => setIsShareLoc(false)}
                            selectedLoc={selectedLoc}
                        />
                    )}

                    {/* Assign Location To Production */}
                    {asignLocData?.id && (
                        <AsignLocationToProd
                            onClose={() => setAsignLocData(null)}
                            onDone={handleApply}
                            locationData={asignLocData}
                        />
                    )}

                    {/* Scouting Details */}
                    {locationDetails?.id && (
                        <ScoutingDetails
                            onClose={() => setLocationDetails(null)}
                            locationData={locationDetails}
                        />
                    )}

                    {/* Create Script */}
                    {showCreateScript && (
                        <CreateScript
                            productionCompanyName={companyName}
                            onClose={() => setShowCreateScript(false)}
                            onSubmit={(payload: any) => {
                                fetchScript();
                                setShowCreateScript(false);
                            }}
                        />
                    )}

                    {/* Create Collection */}
                    {showCreateCollection && (
                        <CreateCollections
                            onClose={() => setShowCreateCollection(false)}
                            onDone={() => {
                                fetchCollection();
                                setShowCreateCollection(false);
                            }}
                        />
                    )}

                    {/* Assign Location To Collection */}
                    {asignLocCollection?.id && (
                        <AsignLocationToCollection
                            onClose={() => setAsignLocCollection(null)}
                            onCreate={() => {
                                setAsignLocCollection(null);
                                setShowCreateCollection(true);
                            }}
                            locationData={asignLocCollection}
                            production_company_name={companyName}
                        />
                    )}

                    {asignLocToScript?.id &&
                        <AsignLocationToPerScript
                            onClose={() => setAsignLocToScript(null)}
                            onCreate={() => {
                                setAsignLocToScript(null);
                            }}
                            locationData={asignLocToScript}
                            production_company_name={getCompanyName(userData)}
                        />
                    }

                    {viewerOpen &&
                        <ImageFullScreenViewer
                            isOpen={viewerOpen}
                            images={perScriptSelectedImages}
                            initialIndex={0}
                            onClose={() => {
                                setViewerOpen(false)
                                setPerScriptSelectedImages([])
                            }}
                            onDownload={emptyFunction}
                            onInfo={emptyFunction}
                            onDelete={emptyFunction}
                        />
                    }

                </div>
            </div>
        </SideBarLayout>
    );
};

export default LocationVault;

// import React, { useCallback, useContext, useEffect, useState } from 'react'
// import SideBarLayout from '../../../global/layout/SideBarLayout'
// import LocationVaultHeader from './LocationVaultHeader';
// import { emptyFunction, getCompanyName, showToast } from '../../../utils';
// import LocationVaultFilterTabs, { TabType } from './LocationVaultFilterTabs';
// import LocationVaultRightAction from './LocationVaultRightAction';
// import LocationVaultGridView from './LocationVaultGridView';
// import LocationVaultListView from './LocationVaultListView';
// import CreateLocation from '../createLocation';
// import { useNavigate, useParams } from 'react-router-dom';
// import { endPoints } from '../../../utils/endpoints';
// import { makeDeleteCall, makeGetCall, makeNextPageCall, makePostCall } from '../../../effects/requests';
// import { ApiResponseType } from '../../../types';
// import { MyContext } from '../../../context';
// import InfiniteScroll from 'react-infinite-scroll-component';
// import Loader from '../../../global/ui/Loader';
// import { debounce } from 'lodash';
// import DeleteLocPopup from './DeleteLocPopup';
// import ScoutingVault from '../scouting/ScoutingVault';
// import LocationSharing from './LocationSharing';
// import { canViewLocation } from '../../../data/data';
// import AsignLocationToProd from './AsignLocationToProd';
// import ScoutingDetails from '../scouting/ScoutingDetails';
// import { toast } from 'react-toastify';
// import PerScript from '../perScript';
// import CreateScript from '../perScript/createScript';
// import Collections from '../collections';
// import CreateCollections from '../collections/CreateCollections';
// import AsignLocationToCollection from '../collections/AsignLocationToCollection';

// const Dummy = () => (<div
//     style={{
//         position: 'absolute',
//         top: '50%',
//         left: '50%',
//         transform: 'translate(-50%, -50%)',
//         fontSize: '18px',
//         color: 'black',
//         textAlign: 'center',
//         width: '100%',
//         padding: '0 16px',
//         boxSizing: 'border-box',
//         fontWeight: 600
//     }}
// >
//     Coming Soon, Please visit <a
//         href="https://www.sixthdegree.io/locations"
//         target="_blank"
//         rel="noopener noreferrer"
//     >
//         www.sixthdegree.io/locations
//     </a> for locations already on baord.
// </div>)

// const LocationVault = () => {

//     const { userData } = useContext(MyContext)

//     const params = useParams();
//     const vaultType = params?.type || '';

//     const [isGridView, setIsGridView] = useState(true);
//     const [showCreateLoc, setShowCreateLoc] = useState(false);
//     const [showCreateScript, setShowCreateScript] = useState(false);
//     const [showCreateCollection, setShowCreateCollection] = useState(false);
//     const [asignLocCollection, setAsignLocCollection] = useState<any>(null);
//     const [isShareLoc, setIsShareLoc] = useState(false);

//     const [allLocationData, setAllLocationData] = useState<any>([])
//     const [nextUrl, setNextUrl] = useState<string | null>(null);
//     const [searchQuery, setSearchQuery] = useState<string | null>('');
//     const [selectedLocId, setSelectedLocId] = useState<any>(null);
//     const [activeTab, setActiveTab] = useState<TabType>('studio_vault');
//     const [selectedLoc, setSelectedLoc] = useState<any>(null);
//     const [asignLocData, setAsignLocData] = useState<any>(null);
//     const [locationDetails, setLocationDetails] = useState<any>(null)
//     const [collectionDetails, setCollectionDetails] = useState<any>(null)

//     const [collectionList, setCollectionList] = useState<any>([])
//     const [perScriptList, setPerScriptList] = useState<any>([])
//     const [scriptNextUrl, setScriptNextUrl] = useState<string | null>(null);
//     const [collectionNextUrl, setCollectionNextUrl] = useState<string | null>(null);

//     const navigate = useNavigate();

//     const fetchLocation = (searchValue = '') => {
//         const params = {
//             production_company_name: getCompanyName(userData),
//             search: searchValue,
//             my_vault: vaultType == 'myvault' ? true : false,
//             collection_id: collectionDetails?.id || ''
//         }
//         makeGetCall(endPoints.LOCATION_VAULT, params).then((res: ApiResponseType<any>) => {
//             if (res?.status?.code === 200) {
//                 setAllLocationData(res?.results);
//                 setNextUrl(res?.next || null);
//                 setAsignLocData(null)
//             }
//         }).catch(emptyFunction)
//     }

//     const fetchCollection = () => {
//         makeGetCall(endPoints.LOCATION_COLLECTION, { production_company_name: getCompanyName(userData) }).then((res: ApiResponseType<any>) => {
//             if (res?.status?.code == 200) {
//                 const data = res?.results?.length > 0 ? res?.results : [];
//                 setCollectionList(data)
//                 setCollectionNextUrl(res?.next || null);
//             }
//         })
//     }

//     const fetchScript = () => {
//         const url = `${endPoints.LOCATION_SCRIPT}?production_company_name=${getCompanyName(userData)}`
//         makeGetCall(url).then((res: ApiResponseType<any>) => {
//             if (res?.status?.code === 200) {
//                 setPerScriptList(res?.results?.length > 0 ? res?.results : []);
//                 setScriptNextUrl(res?.next || null);
//             }
//         }).catch(emptyFunction)
//     }

//     const fetchMoreData = () => {
//         if (!nextUrl) return;
//         makeNextPageCall(nextUrl, false, true).then((res: ApiResponseType<any>) => {
//             const data = [...allLocationData, ...res.results];
//             setAllLocationData(data);
//             setNextUrl(res?.next);
//         })
//     }

//     const fetchMoreScript = () => {
//         if (!scriptNextUrl) return;
//         makeNextPageCall(scriptNextUrl, false, true).then((res: ApiResponseType<any>) => {
//             const data = [...perScriptList, ...res.results];
//             setPerScriptList(data);
//             setScriptNextUrl(res?.next);
//         })
//     }

//     const fetchMoreCollection = () => {
//         if (!collectionNextUrl) return;
//         makeNextPageCall(collectionNextUrl, false, true).then((res: ApiResponseType<any>) => {
//             const data = [...collectionList, ...res.results];
//             setCollectionList(data);
//             setCollectionNextUrl(res?.next);
//         })
//     }

//     const handleRemoveLoc = () => {
//         const url = `${endPoints.LOCATION_VAULT}${selectedLocId}/`
//         makeDeleteCall(url).then((res: ApiResponseType<any>) => {
//             if (res?.status?.code === 200) {
//                 showToast(res?.status?.message, "success")
//                 setAllLocationData((prev: any) => prev?.filter((location: any) => location?.id !== selectedLocId));
//             }
//             setSelectedLocId(null)
//         }).catch(() => setSelectedLocId(null))
//     };

//     const moveItem = (from: number, to: number) => {
//         setAllLocationData((prev: any) => {
//             const updated = [...prev];
//             const [removed] = updated.splice(from, 1);
//             updated.splice(to, 0, removed);
//             return updated;
//         });
//     };

//     const onAsign = (data: any) => {
//         if (vaultType == 'vault') {
//             setAsignLocCollection(data)
//             return
//         }
//         setAsignLocData(data);
//     }

//     const handleApply = async (selectedData: any, locationId: any) => {
//         if (!selectedData?.id) {
//             showToast('Please add at least one production', 'error');
//             return
//         }
//         const payload = {
//             production_company_id: selectedData?.production_company?.id,
//             location_vault_id: locationId
//         }
//         const toastId = toast.loading('Duplicating location vault...', {
//             position: 'bottom-right',
//             autoClose: false,
//             closeOnClick: false,
//             closeButton: false,
//         });
//         setAsignLocData(null)
//         const res: ApiResponseType<any> = await makePostCall(endPoints.DUPLICATE_LOCATION_VAULT, payload, false, true);
//         toast.dismiss(toastId);
//         if (res?.status?.code == 200) {
//             showToast(res?.status?.message, 'success');
//             fetchLocation();
//         }
//     }

//     const onChangeTextDebounce = useCallback(debounce(fetchLocation, 800), []);

//     useEffect(() => {
//         if (userData?.id) {
//             if (activeTab === 'studio_vault' || activeTab === 'scouting') {
//                 fetchLocation();
//             }
//             if (activeTab === 'per_script') {
//                 fetchScript();
//             }
//             if (activeTab === 'collections') {
//                 fetchCollection();
//             }
//         }
//     }, [userData?.id, vaultType, activeTab]);

//     return (
//         <SideBarLayout>
//             <div className="right-content-full-screen">
//                 <div id='scrollableId' className="lv-page">

//                     <LocationVaultHeader
//                         onCreate={(type: any) => {
//                             if (type == 'location') {
//                                 setShowCreateLoc(true)
//                                 return
//                             }
//                             if (type == 'script') {
//                                 setShowCreateScript(true)
//                                 return
//                             }
//                             if (type == 'collection') {
//                                 setShowCreateCollection(true)
//                                 return
//                             }
//                         }}
//                         query={searchQuery}
//                         onSearch={(value: any) => {
//                             setSearchQuery(value);
//                             onChangeTextDebounce(value);
//                         }}
//                         vaultType={vaultType}
//                     />

//                     <div className="lv-tabs-bar">

//                         <LocationVaultFilterTabs
//                             activeTab={activeTab}
//                             setActiveTab={(type: any) => {
//                                 setActiveTab(type);
//                                 setCollectionDetails(null)
//                             }}
//                         />

//                         <LocationVaultRightAction
//                             isGridView={isGridView}
//                             onViewSwitch={setIsGridView}
//                             onEdit={emptyFunction}
//                             isScouting={activeTab == 'scouting'}
//                             hideViewToggle={activeTab == 'collections' || activeTab == 'per_script'}
//                         />
//                     </div>

//                     {collectionDetails?.name &&
//                         <div style={{ marginBottom: 10 }} className="ld-breadcrumb">
//                             <button className="ld-breadcrumb__link">
//                                 Collection
//                             </button>
//                             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                                 <polyline points="9 18 15 12 9 6" />
//                             </svg>
//                             <span className="ld-breadcrumb__current">{collectionDetails?.name || ''}</span>
//                         </div>
//                     }

//                     {activeTab === 'studio_vault'
//                         ?
//                         <InfiniteScroll
//                             dataLength={allLocationData?.length}
//                             next={fetchMoreData}
//                             hasMore={nextUrl ? true : false}
//                             loader={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader imgStyle={{ width: '40px', height: '40px' }} /></div>}
//                             scrollableTarget="scrollableId"
//                         >

//                             {isGridView
//                                 ?
//                                 <LocationVaultGridView
//                                     onView={(location: any) =>
//                                         navigate(`/location-folders/${vaultType}/${location?.id}`, {
//                                             state: { location: { ...location?.location, role: location?.role, can_view_location: location?.can_view_location || canViewLocation } },
//                                         })
//                                     }
//                                     onImageView={(location: any) => navigate(`/location-folders/${vaultType}/${location?.id}`, {
//                                         state: { location: { ...location?.location, role: location?.role, can_view_location: location?.can_view_location || canViewLocation } },
//                                     })}
//                                     allLocationData={allLocationData}
//                                     onDelete={(loc: any) => setSelectedLocId(loc?.id)}
//                                     moveItem={moveItem}
//                                     onShare={(data: any) => {
//                                         setSelectedLoc(data);
//                                         setIsShareLoc(true)
//                                     }}
//                                     vaultType={vaultType}
//                                     onAsign={onAsign}
//                                     hideAsign={collectionDetails?.id}
//                                 />
//                                 :
//                                 <LocationVaultListView
//                                     onView={(location: any) =>
//                                         navigate(`/location-folders/${vaultType}/${location?.id}`, {
//                                             state: { location: { ...location?.location, role: location?.role, can_view_location: location?.can_view_location || canViewLocation } },
//                                         })
//                                     }
//                                     onImageView={(location: any) => navigate(`/location-folders/${vaultType}/${location?.id}`, {
//                                         state: { location: { ...location?.location, role: location?.role, can_view_location: location?.can_view_location || canViewLocation } },
//                                     })}
//                                     allLocationData={allLocationData}
//                                     onDelete={(loc: any) => setSelectedLocId(loc?.id)}
//                                     moveItem={moveItem}
//                                     onShare={(data: any) => {
//                                         setSelectedLoc(data);
//                                         setIsShareLoc(true)
//                                     }}
//                                     vaultType={vaultType}
//                                     onAsign={onAsign}
//                                     hideAsign={collectionDetails?.id}
//                                 />
//                             }

//                         </InfiniteScroll>
//                         :

//                         activeTab == 'scouting'

//                             ?

//                             <InfiniteScroll
//                                 dataLength={allLocationData?.length}
//                                 next={fetchMoreData}
//                                 hasMore={nextUrl ? true : false}
//                                 loader={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader imgStyle={{ width: '40px', height: '40px' }} /></div>}
//                                 scrollableTarget="scrollableId"
//                             >

//                                 {isGridView
//                                     ?

//                                     <LocationVaultGridView
//                                         onView={setLocationDetails}
//                                         onImageView={setLocationDetails}
//                                         allLocationData={allLocationData}
//                                         onDelete={(loc: any) => setSelectedLocId(loc?.id)}
//                                         moveItem={moveItem}
//                                         isScouted
//                                         onShare={(data: any) => {
//                                             setSelectedLoc(data);
//                                             setIsShareLoc(true)
//                                         }}
//                                         vaultType={vaultType}
//                                         onAsign={onAsign}
//                                     />

//                                     :

//                                     <ScoutingVault
//                                         allLocationData={allLocationData}
//                                         onShare={(data: any) => {
//                                             setSelectedLoc(data);
//                                             setIsShareLoc(true)
//                                         }}
//                                         vaultType={vaultType}
//                                         onAsign={onAsign}
//                                     />
//                                 }

//                             </InfiniteScroll>
//                             :

//                             activeTab === 'per_script'

//                                 ?

//                                 <InfiniteScroll
//                                     dataLength={perScriptList?.length}
//                                     next={fetchMoreScript}
//                                     hasMore={scriptNextUrl ? true : false}
//                                     loader={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader imgStyle={{ width: '40px', height: '40px' }} /></div>}
//                                     scrollableTarget="scrollableId"
//                                 >
//                                     <PerScript
//                                         allData={perScriptList}
//                                         onRefresh={fetchScript}
//                                     />
//                                 </InfiniteScroll>

//                                 :

//                                 activeTab === 'collections'

//                                     ?

//                                     <InfiniteScroll
//                                         dataLength={collectionList?.length}
//                                         next={fetchMoreCollection}
//                                         hasMore={collectionNextUrl ? true : false}
//                                         loader={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader imgStyle={{ width: '40px', height: '40px' }} /></div>}
//                                         scrollableTarget="scrollableId"
//                                     >
//                                         <Collections
//                                             allData={collectionList}
//                                             onRefresh={fetchCollection}
//                                         />
//                                     </InfiniteScroll>

//                                     :

//                                     <Dummy />
//                     }
//                     {showCreateLoc && (
//                         <CreateLocation
//                             onClose={() => {
//                                 setShowCreateLoc(false)
//                             }}
//                             onDone={() => {
//                                 setShowCreateLoc(false)
//                                 fetchLocation();
//                             }}
//                             vaultType={vaultType}
//                         />
//                     )}

//                     {selectedLocId &&
//                         <DeleteLocPopup
//                             onClose={() => setSelectedLocId(null)}
//                             onDelete={handleRemoveLoc}
//                         />
//                     }

//                     {isShareLoc &&
//                         <LocationSharing
//                             isOpen={isShareLoc}
//                             onClose={() => setIsShareLoc(false)}
//                             selectedLoc={selectedLoc}
//                         />
//                     }

//                     {asignLocData?.id &&
//                         <AsignLocationToProd
//                             onClose={() => setAsignLocData(null)}
//                             onDone={handleApply}
//                             locationData={asignLocData}
//                         />
//                     }

//                     {locationDetails?.id &&
//                         <ScoutingDetails
//                             onClose={() => setLocationDetails(null)}
//                             locationData={locationDetails}
//                         />
//                     }

//                     {showCreateScript &&
//                         <CreateScript
//                             productionCompanyName={getCompanyName(userData)}
//                             onClose={() => setShowCreateScript(false)}
//                             onSubmit={(payload: any) => {
//                                 fetchScript();
//                                 setShowCreateScript(false);
//                             }}
//                         />
//                     }

//                     {showCreateCollection &&
//                         <CreateCollections
//                             onClose={() => setShowCreateCollection(false)}
//                             onDone={() => {
//                                 fetchCollection()
//                                 setShowCreateCollection(false)
//                             }}
//                         />
//                     }

//                     {asignLocCollection?.id &&
//                         <AsignLocationToCollection
//                             onClose={() => setAsignLocCollection(null)}
//                             onCreate={() => {
//                                 setAsignLocCollection(null);
//                                 setShowCreateCollection(true)
//                             }}
//                             locationData={asignLocCollection}
//                             production_company_name={getCompanyName(userData)}
//                         />
//                     }

//                 </div>
//             </div>
//         </SideBarLayout>
//     )
// }

// export default LocationVault